import { useEffect, useState } from "react";
import { SkeletonList } from "./Skeleton";
import { Pagination, PAGE_SIZE } from "./Pagination";

interface AuditRow {
  ts: string;
  type: string;
  userEmail: string;
  detail: string | null;
  sessionTitle: string | null;
  ownerEmail: string | null;
  model: string | null;
  costUsd: number | null;
}

const TYPE_PT: Record<string, string> = {
  register: "Cadastro",
  login: "Login",
  login_failed: "Login recusado",
  login_blocked: "Login bloqueado",
  logout: "Logout",
  ask: "Pergunta",
  regenerate: "Regeneração",
  docgen: "Documentação automática",
  approve: "Ação administrativa",
  reject: "Rejeição",
  deactivate: "Desativação",
  view_other: "Acesso a conversa de terceiro",
  blocked: "Acesso negado",
};



/**
 * Trilha de auditoria da org: quem fez o quê e quando - aprovações, mudanças
 * de plano, pacotes, logins, acessos a conversas de terceiros etc. Leitura do
 * log de atividade que o backend grava em toda ação.
 */
export function AuditPanel({ orgId }: { orgId?: string }) {
  const [items, setItems] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(h);
  }, [search]);
  useEffect(() => {
    setPage(1);
  }, [debounced, typeFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (orgId) params.set("orgId", orgId);
    params.set("offset", String((page - 1) * PAGE_SIZE));
    params.set("limit", String(PAGE_SIZE));
    if (typeFilter) params.set("type", typeFilter);
    if (debounced) params.set("q", debounced);
    fetch(`/api/admin/audit?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : { items: [], total: 0 }))
      .then((j: { items?: AuditRow[]; total?: number }) => {
        if (!cancelled) {
          setItems(j.items ?? []);
          setTotal(j.total ?? 0);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, page, typeFilter, debounced]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fmtTs = (iso: string) =>
    new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium", timeZone: "America/Sao_Paulo" });

  return (
    <div className="admin-block">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Auditoria ({total})</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <select className="select-sm" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Filtrar por tipo de evento">
            <option value="">Todos os eventos</option>
            {Object.entries(TYPE_PT).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por e-mail ou detalhe…"
            style={{ maxWidth: 240, padding: "5px 8px", fontSize: 12.5 }}
          />
        </div>
      </div>
      <p className="admin-user-meta" style={{ marginBottom: 10 }}>
        Quem fez o quê e quando: aprovações, plano, pacotes, faturas, logins e acessos. Registro automático de toda ação.
      </p>
      {loading ? (
        <SkeletonList rows={8} />
      ) : items.length === 0 ? (
        <div className="admin-empty small">Nenhum evento encontrado.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table" style={{ width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Quando</th>
                <th style={{ textAlign: "left" }}>Quem</th>
                <th style={{ textAlign: "left" }}>Evento</th>
                <th style={{ textAlign: "left" }}>Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e, i) => (
                <tr key={`${e.ts}-${i}`}>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtTs(e.ts)}</td>
                  <td>{e.userEmail}</td>
                  <td>
                    <span className={`badge ${e.type === "blocked" || e.type === "login_failed" || e.type === "login_blocked" ? "err" : e.type === "view_other" ? "warn" : ""}`}>
                      {TYPE_PT[e.type] ?? e.type}
                    </span>
                  </td>
                  <td>
                    {e.detail ?? (e.sessionTitle ? `"${e.sessionTitle}"${e.ownerEmail ? ` (dona: ${e.ownerEmail})` : ""}` : "")}
                    {e.costUsd != null && e.costUsd > 0 && (
                      <span className="admin-user-meta" style={{ marginLeft: 6 }}>${e.costUsd.toFixed(4)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageCount={pageCount} onChange={setPage} label={`${total} evento(s)`} />
        </div>
      )}
    </div>
  );
}
